import React, { useState, useEffect } from 'react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

import { Calendar as CalendarIcon } from "lucide-react";

import Moment from 'moment';
import { motion } from "framer-motion";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import * as XLSX from 'xlsx';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';

import ServiceOutlet from 'services/serviceOutlet';
import ServiceAuth from 'services/serviceAuth';

export default function PaneStaff(props) {

    const [records, setRecords] = useState([]);

    const [permissions, setPermissions] = useState([]);
    const [modules, setModules] = useState([]);

    const [tempDoc, setTempDoc] = useState({});
    const [filter, setFilter] = useState({});

    const [date, setDate] = useState({
        from: Moment().startOf('month').toDate(),
        to: Moment().toDate(),
    });

    const [tab, setTab] = useState('all');
    const [query, setQuery] = useState('');
    const [eid, setEid] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [pageCurrent, setPageCurrent] = useState(0);
    const [pageTotal, setPageTotal] = useState(0);

    const [isDate, setIsDate] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [submit, setSubmit] = useState(false);

    const outletService = new ServiceOutlet();
    const authService = new ServiceAuth();

    const userTypes = [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'MANAGER', label: 'Manger' },
        { value: 'STAFF', label: 'Staff' },
    ];

    const formVSchema = Yup.object().shape({
        role: Yup.string().required('This information is required'),
        fname: Yup.string().required('This information is required'),
        mname: Yup.string().required('This information is required'),
        lname: Yup.string().required('This information is required'),
        email: Yup.string().required('This information is required').email('Please enter valid email address'),
        phone: Yup.string().required('This information is required').matches(/^\d{10}$/, 'Phone number must be 10 digits'),
        street_1: Yup.string().required('This information is required'),
        street_2: Yup.string().required('This information is required'),
        city: Yup.string().required('This information is required'),
        state: Yup.string().required('This information is required'),
        country: Yup.string().required('This information is required'),
        postal_code: Yup.string().required('This information is required').matches(/^\d{6}$/, 'Phone number must be 10 digits'),
    });

    const { values, errors, touched, handleChange, handleSubmit, handleReset, resetForm, setErrors, setValues } = useFormik({
        initialValues: {
            role: '',
            fname: '',
            mname: '',
            lname: '',
            email: '',
            phone: '',
            street_1: '',
            street_2: '',
            city: '',
            state: '',
            country: '',
            postal_code: '',
        },
        validationSchema: formVSchema,
        enableReinitialize: true,
        onSubmit: values => {
            if (isOpen && !isEdit) {
                setSubmit(true);
                var body = {
                    "orgs": props.outlet.orgid,
                    "outletid": props.outlet._id,
                    "fname": values.fname,
                    "mname": values.mname,
                    "lname": values.lname,
                    "email": values.email,
                    "phone": values.phone,
                    "dob": "1995-12-23T00:00:00.000Z",
                    "addresses": [{
                        title: "HOME",
                        default: true,
                        street_1: values.street_1,
                        street_2: values.street_2,
                        city: values.city.toUpperCase(),
                        state: values.state.toUpperCase(),
                        country: values.country.toUpperCase(),
                        postal_code: values.postal_code,
                    }],
                    "role": values.role,
                    "modules": modules,
                    "permissions": permissions,
                    "password": values.phone.toString(),
                    "media": {},
                    "status": "verify"
                };
                outletService.registerPOSUser(body, props.outlet.orgid).then((res) => {
                    if (res.status) {
                        resetForm();
                        setIsOpen(false);
                        setModules([]);
                        setPermissions([]);
                        loadData();
                        toast.success('New staff added.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    } else {
                        toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    }
                    setSubmit(false);
                });
            }

            if (isOpen && isEdit) {
                setSubmit(true);
                let body = {
                    uid: eid,
                    data: {
                        fname: values.fname,
                        mname: values.mname,
                        lname: values.lname,
                        email: values.email,
                        phone: values.phone,
                        addresses: [
                            {
                                title: "HOME",
                                default: true,
                                street_1: values.street_1,
                                street_2: values.street_2,
                                city: values.city.toUpperCase(),
                                state: values.state.toUpperCase(),
                                country: values.country.toUpperCase(),
                                postal_code: values.postal_code,
                            }
                        ],
                        media: {},
                        role: values.role,
                        modules: modules,
                        permissions: permissions,
                    }
                };
                outletService.updatePOSUser(body, props.outlet.orgid).then((res) => {
                    if (res.status) {
                        resetForm();
                        setIsOpen(false);
                        setTempDoc({});
                        setEid('');
                        setIsEdit(false);
                        setModules([]);
                        setPermissions([]);
                        loadData();
                        toast.success('Staff updated.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    } else {
                        toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    }
                    setSubmit(false);
                });
            }
        }
    });

    const switchModule = (module) => {
        let tempModule = [...modules];
        if (tempModule.includes(module)) {
            var index = tempModule.findIndex((e) => e === module);
            tempModule.splice(index, 1);
            setPermissions((prev) =>
                prev.filter((p) => !p.includes(module))
            );
            togglePermission(`${module}.NONE`);
        } else {
            tempModule.push(module);
        }
        setModules(tempModule);
    }

    const togglePermission = (permission) => {
        setPermissions((prev) =>
            prev.includes(permission)
                ? prev.filter((p) => p !== permission)
                : [...prev, permission]
        );
    };

    const addMultiplePermissions = (perms) => {
        setPermissions((prev) => [
            ...new Set([...prev, ...perms])
        ]);
    };

    const removeMultiplePermissions = (perms) => {
        setPermissions((prev) =>
            prev.filter((p) => !perms.includes(p))
        );
    };

    const onCheck = (permission) => {
        switch (permission) {
            case "TERMINAL.ALL":
                if (permissions.includes("TERMINAL.ALL")) {
                    addMultiplePermissions(["TERMINAL.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["TERMINAL.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "ORDERS.ALL":
                if (permissions.includes("ORDERS.ALL")) {
                    addMultiplePermissions(["ORDERS.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["ORDERS.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "INVOICES.ALL":
                if (permissions.includes("INVOICES.ALL")) {
                    addMultiplePermissions(["INVOICES.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["INVOICES.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "REPORTS.ALL":
                if (permissions.includes("REPORTS.ALL")) {
                    addMultiplePermissions(["REPORTS.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["REPORTS.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "SETTINGS.ALL":
                if (permissions.includes("SETTINGS.ALL")) {
                    addMultiplePermissions(["SETTINGS.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["SETTINGS.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "PROFILE.ALL":
                if (permissions.includes("PROFILE.ALL")) {
                    addMultiplePermissions(["PROFILE.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["PROFILE.NONE"]);
                    addMultiplePermissions(["PROFILE.ALL", "PROFILE.ONLY"]);
                }
                break;
            case "PROFILE.ONLY":
                if (permissions.includes("PROFILE.ALL")) {
                    addMultiplePermissions(["PROFILE.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["PROFILE.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "KDS.ALL":
                if (permissions.includes("KDS.ALL")) {
                    addMultiplePermissions(["KDS.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["KDS.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "BOOKING.ALL":
                if (permissions.includes("BOOKING.ALL")) {
                    addMultiplePermissions(["BOOKING.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["BOOKING.NONE"]);
                    togglePermission(permission);
                }
                break;
            case "INVENTORY.ALL":
                if (permissions.includes("INVENTORY.ALL")) {
                    addMultiplePermissions(["INVENTORY.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["INVENTORY.NONE"]);
                    addMultiplePermissions(["INVENTORY.ALL", "INVENTORY.ONLY"]);
                }
                break;
            case "INVENTORY.ONLY":
                if (permissions.includes("INVENTORY.ALL")) {
                    addMultiplePermissions(["INVENTORY.NONE"]);
                    togglePermission(permission);
                } else {
                    removeMultiplePermissions(["INVENTORY.NONE"]);
                    togglePermission(permission);
                }
                break;
            default:
                togglePermission(permission);
                break;
        }
    }

    const changeTab = (val) => {
        setTab(val);
        setQuery('');
        setDate({
            from: Moment().startOf('year').toDate(),
            to: Moment().toDate(),
        });
        switch (val) {
            case "all":
                setFilter({});
                break;
            case "verify":
                setFilter({ 'status': 'verify' });
                break;
            case "active":
                setFilter({ 'status': 'active' });
                break;
            case "banned":
                setFilter({ 'status': 'banned' });
                break;
            default:
                break;
        }
    }

    const handleOnChange = (e) => {
        var { value, name } = e.target;
        switch (name) {
            case 'pageSize':
                setPageSize(value);
                break;
            case 'search':
                setQuery(value);
                break;
        }
    }

    const onDateChange = (event) => {
        setIsDate(true);
        setDate(event);
    }

    const add = () => {
        setTempDoc({});
        setEid('');
        setIsEdit(false);
        setModules([]);
        setPermissions([]);
        setIsOpen(true);
    }

    const updateStatus = (userid, status) => {
        let body = {
            uid: userid,
            data: {
                status: status
            }
        }
        outletService.updateStaffPosUser(body, props.outlet.orgid).then((res) => {
            if (res['status']) {
                loadData();
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        });
    }

    const changePassword = (id) => {
        authService.updatePosPassword({ uid: id }).then((res) => {
            if (res.status) {
                toast.success('Password reset link sent to registered email address.', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            } else {
                toast.error(res.msg, { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
            setSubmit(false);
        });
    }

    const editRecord = (doc) => {
        setTempDoc(doc);
        setEid(doc.userid);
        setIsEdit(true);
        setModules(doc.modules);
        setPermissions(doc.permissions);
        setIsOpen(true);
    }

    const pageChange = (pageValue) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": pageValue,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : "",
                "secure": false,
            };
            var res = await outletService.getAllOutletStaff(body, props.outlet.orgid);
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setRecords(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const loadData = () => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 1,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : "",
                "secure": false,
            };
            var res = await outletService.getAllOutletStaff(body, props.outlet.orgid);
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setRecords(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const downloadFile = ({ data, fileName, fileType }) => {
        const blob = new Blob([data], { type: fileType })
        const a = document.createElement('a')
        a.download = fileName
        a.href = window.URL.createObjectURL(blob)
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        })
        a.dispatchEvent(clickEvt)
        a.remove()
    }

    const exportData = (type) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 0,
                "size": 0,
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? Moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await outletService.getAllOutletStaff(body, props.outlet.orgid);
            var records = res.data.data;
            let items = [];
            records.forEach((item) => {
                items.push({
                    fname: item.profile.fname,
                    mname: item.profile.mname,
                    lname: item.profile.lname,
                    email: item.profile.email,
                    phone: item.profile.phone,
                    dob: item.profile.dob,
                    role: item.role,
                    status: item.status,
                    createdAt: item.createdAt,
                });
            });

            if (type === "xlsx") {
                const worksheet = XLSX.utils.json_to_sheet(items);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "users");
                XLSX.writeFile(workbook, "Staff.xlsx");
            } else {
                let headers = ['fname, mname, lname, email, phone, dob, role, status, createdAt'];
                let recordCsv = items.reduce((acc, record) => {
                    const { fname, mname, lname, email, phone, dob, role, status, createdAt } = record
                    acc.push([fname, mname, lname, email, phone, dob, role, status, createdAt].join(','))
                    return acc
                }, []);
                downloadFile({
                    data: [...headers, ...recordCsv].join('\n'),
                    fileName: 'Staff.csv',
                    fileType: 'text/csv',
                });
            }
        })();
    }

    const nextPage = () => {
        if (pageCurrent < pageTotal) {
            pageChange(pageCurrent + 1);
        }
    }

    const prevPage = () => {
        if (pageCurrent > 1) {
            pageChange(pageCurrent - 1);
        }
    }

    const populdata = () => {
        setValues({
            role: tempDoc.role,
            fname: tempDoc.profile.fname,
            mname: tempDoc.profile.mname,
            lname: tempDoc.profile.lname,
            email: tempDoc.profile.email,
            phone: tempDoc.profile.phone,
            street_1: tempDoc.profile.addresses[0] === undefined ? "" : tempDoc.profile.addresses[0].street_1,
            street_2: tempDoc.profile.addresses[0] === undefined ? "" : tempDoc.profile.addresses[0].street_2,
            city: tempDoc.profile.addresses[0] === undefined ? "" : tempDoc.profile.addresses[0].city,
            state: tempDoc.profile.addresses[0] === undefined ? "" : tempDoc.profile.addresses[0].state,
            country: tempDoc.profile.addresses[0] === undefined ? "" : tempDoc.profile.addresses[0].country,
            postal_code: tempDoc.profile.addresses[0] === undefined ? "" : tempDoc.profile.addresses[0].postal_code,
        });
        setModules(tempDoc.modules);
        setPermissions(tempDoc.permissions);
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [pageSize, query, date, filter]);

    useEffect(() => {
        if (isOpen && isEdit) {
            populdata();
        }
    }, [isOpen, isEdit, tempDoc]);

    return (
        <>
            {
                isOpen && <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[70%] min-h-screen absolute right-0 p-[20px]'>
                        <ScrollArea>
                            <div className='px-2'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>{isEdit ? "UPDATE" : "ADD"} STAFF</h1>
                                    <div className='w-7 h-7 text-gray-700 flex items-center justify-center border border-black rounded-full hover:bg-black hover:text-white cursor-pointer' onClick={() => {
                                        setIsOpen(false);
                                        resetForm();
                                    }}>
                                        <span><i className="las la-times text-xl cursor-pointer"></i></span>
                                    </div>
                                </div>
                                <hr className='my-2' />
                                <form className='mt-4 grid grid-cols-12 gap-4' onSubmit={handleSubmit} onReset={handleReset} noValidate="" autoComplete='off'>
                                    <div className="col-span-4">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">Staff Type<sup className="text-red-600">*</sup></label>
                                        <select id="role" className="border-gray-300 border rounded appearance-none w-full py-1 px-2 bg-white mt-2" onChange={handleChange} value={values.role}>
                                            <option value="" disabled="" selected="selected">Select</option>
                                            {
                                                userTypes.length !== 0 && userTypes.map((item) => {
                                                    return <option value={item.value} selected={values.role == item.value}>{item.label}</option>
                                                })
                                            }
                                        </select>
                                        {(errors.role && touched.role) && <p className='text-xs text-red-400 mt-1'>{errors.role}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">First Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="fname" value={values.fname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Jhon" onChange={handleChange} />
                                        {(errors.fname && touched.fname) && <p className='text-xs text-red-400 mt-1'>{errors.fname}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Maiden Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="mname" value={values.mname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Sam" onChange={handleChange} />
                                        {(errors.mname && touched.mname) && <p className='text-xs text-red-400 mt-1'>{errors.mname}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Last Name<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="lname" value={values.lname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Deo" onChange={handleChange} />
                                        {(errors.lname && touched.lname) && <p className='text-xs text-red-400 mt-1'>{errors.lname}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Email Address<sup className="text-red-600">*</sup></label>
                                        <input type="email" id="email" value={values.email} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. jhon.deo@gmail.com" onChange={handleChange} />
                                        {(errors.email && touched.email) && <p className='text-xs text-red-400 mt-1'>{errors.email}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Phone Number<sup className="text-red-600">*</sup></label>
                                        <input type="number" id="phone" value={values.phone} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                        {(errors.phone && touched.phone) && <p className='text-xs text-red-400 mt-1'>{errors.phone}</p>}
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Street Address 1<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="street_1" value={values.street_1} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Apt, Building " onChange={handleChange} />
                                        {(errors.street_1 && touched.street_1) && <p className='text-xs text-red-400 mt-1'>{errors.street_1}</p>}
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Street Address 2<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="street_2" value={values.street_2} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Street address, Landmark" onChange={handleChange} />
                                        {(errors.street_2 && touched.street_2) && <p className='text-xs text-red-400 mt-1'>{errors.street_2}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">City<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="city" value={values.city} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Mumbai" onChange={handleChange} />
                                        {(errors.city && touched.city) && <p className='text-xs text-red-400 mt-1'>{errors.city}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">State<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="state" value={values.state} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Maharashtra" onChange={handleChange} />
                                        {(errors.state && touched.state) && <p className='text-xs text-red-400 mt-1'>{errors.state}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Country<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="country" value={values.country} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. India" onChange={handleChange} />
                                        {(errors.country && touched.country) && <p className='text-xs text-red-400 mt-1'>{errors.country}</p>}
                                    </div>

                                    <div className="col-span-4">
                                        <label className="block mb-2 text-sm font-medium text-gray-600">Postal code<sup className="text-red-600">*</sup></label>
                                        <input type="text" id="postal_code" value={values.postal_code} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxx" onChange={handleChange} />
                                        {(errors.postal_code && touched.postal_code) && <p className='text-xs text-red-400 mt-1'>{errors.postal_code}</p>}
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">TERMINAL MODULE<sup className="text-red-600">*</sup></label>
                                        <div className='mt-1 flex items-center'>
                                            <Switch checked={modules.includes('TERMINAL')} onCheckedChange={(e) => { switchModule('TERMINAL'); }} className="mr-6" />
                                            {
                                                modules.includes('TERMINAL') && <div className='flex items-center space-x-2'>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('TERMINAL.ALL')} onCheckedChange={(e) => { onCheck('TERMINAL.ALL'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>TERMINAL_ALL</h2>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">ORDERS MODULE<sup className="text-red-600">*</sup></label>
                                        <div className='mt-1 flex items-center'>
                                            <Switch checked={modules.includes('ORDERS')} onCheckedChange={(e) => { switchModule('ORDERS'); }} className="mr-6" />
                                            {
                                                modules.includes('ORDERS') && <div className='flex items-center space-x-2'>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('ORDERS.ALL')} onCheckedChange={(e) => { onCheck('ORDERS.ALL'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>ORDERS_ALL</h2>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">INVOICES MODULE<sup className="text-red-600">*</sup></label>
                                        <div className='mt-1 flex items-center'>
                                            <Switch checked={modules.includes('INVOICES')} onCheckedChange={(e) => { switchModule('INVOICES'); }} className="mr-6" />
                                            {
                                                modules.includes('INVOICES') && <div className='flex items-center space-x-2'>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('INVOICES.ALL')} onCheckedChange={(e) => { onCheck('INVOICES.ALL'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>INVOICES_ALL</h2>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">REPORTS MODULE<sup className="text-red-600">*</sup></label>
                                        <div className='mt-1 flex items-center'>
                                            <Switch checked={modules.includes('REPORTS')} onCheckedChange={(e) => { switchModule('REPORTS'); }} className="mr-6" />
                                            {
                                                modules.includes('REPORTS') && <div className='flex items-center space-x-2'>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('REPORTS.ALL')} onCheckedChange={(e) => { onCheck('REPORTS.ALL'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>REPORTS_ALL</h2>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">PROFILE MODULE<sup className="text-red-600">*</sup></label>
                                        <div className='mt-1 flex items-center'>
                                            <Switch checked={modules.includes('PROFILE')} onCheckedChange={(e) => { switchModule('PROFILE'); }} className="mr-6" />
                                            {
                                                modules.includes('PROFILE') && <div className='flex items-center space-x-2'>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('PROFILE.ALL')} onCheckedChange={(e) => { onCheck('PROFILE.ALL'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>PROFILE_ALL</h2>
                                                    </div>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('PROFILE.ONLY')} onCheckedChange={(e) => { onCheck('PROFILE.ONLY'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>PROFILE_ONLY</h2>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div className="col-span-12">
                                        <label className="block text-xs font-medium text-gray-700 font-mono">SETTINGS MODULE<sup className="text-red-600">*</sup></label>
                                        <div className='mt-1 flex items-center'>
                                            <Switch checked={modules.includes('SETTINGS')} onCheckedChange={(e) => { switchModule('SETTINGS'); }} className="mr-6" />
                                            {
                                                modules.includes('SETTINGS') && <div className='flex items-center space-x-2'>
                                                    <div className='flex items-center'>
                                                        <Checkbox checked={permissions.includes('SETTINGS.ALL')} onCheckedChange={(e) => { onCheck('SETTINGS.ALL'); }} />
                                                        <h2 className='text-sm font-medium ml-2'>SETTINGS_ALL</h2>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    {
                                        props.sub.features.isKds && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">KDS MODULE<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('KDS')} onCheckedChange={(e) => { switchModule('KDS'); }} className="mr-6" />
                                                {
                                                    modules.includes('KDS') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('KDS.ALL')} onCheckedChange={(e) => { onCheck('KDS.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>KDS_ALL</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    {
                                        props.sub.features.isRoom && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">BOOKING MODULE<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('BOOKING')} onCheckedChange={(e) => { switchModule('BOOKING'); }} className="mr-6" />
                                                {
                                                    modules.includes('BOOKING') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('BOOKING.ALL')} onCheckedChange={(e) => { onCheck('BOOKING.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>BOOKING_ALL</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    {
                                        props.sub.features.isInv && <div className="col-span-12">
                                            <label className="block text-xs font-medium text-gray-700 font-mono">INVENTORY MODULE<sup className="text-red-600">*</sup></label>
                                            <div className='mt-1 flex items-center'>
                                                <Switch checked={modules.includes('INVENTORY')} onCheckedChange={(e) => { switchModule('INVENTORY'); }} className="mr-6" />
                                                {
                                                    modules.includes('INVENTORY') && <div className='flex items-center space-x-2'>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('INVENTORY.ALL')} onCheckedChange={(e) => { onCheck('INVENTORY.ALL'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>INVENTORY_ALL</h2>
                                                        </div>
                                                        <div className='flex items-center'>
                                                            <Checkbox checked={permissions.includes('INVENTORY.ONLY')} onCheckedChange={(e) => { onCheck('INVENTORY.ONLY'); }} />
                                                            <h2 className='text-sm font-medium ml-2'>INVENTORY_ONLY</h2>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }

                                    <div className='col-span-12 my-6'>
                                        <div className='flex items-end'>
                                            <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                                {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                                {!submit && "SAVE"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </ScrollArea >
                    </div >
                </div >
            }
            <ScrollArea className='w-full'>
                <div className="w-full h-full">
                    <div className="grid grid-cols-12 grid-gap-2">
                        <div class="col-span-12 flex justify-between items-center">
                            <div className='h-10 bg-gray-100 rounded'>
                                <ul className="intro-y flex text-sm font-sans">
                                    <li className={`px-3 h-6 m-2 rounded flex items-center justify-center ${tab === 'all' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('all') }}>All</button></li>
                                    <li className={`px-3 h-6 m-2 rounded flex items-center justify-center ${tab === 'active' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('active') }}>Active</button></li>
                                    <li className={`px-3 h-6 m-2 rounded flex items-center justify-center ${tab === 'banned' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('banned') }}>Banned</button></li>
                                </ul>
                            </div>
                            <button className="h-8 py-1 px-2 rounded-md bg-prime hover:bg-primeLight text-white mr-2 text-sm" onClick={() => { add() }}><i class="las la-plus mr-1"></i> ADD STAFF</button>
                        </div>
                    </div>
                    <div className="col-span-12 mt-4">
                        <div className="w-full shadow-md rounded-md border">
                            <table className="min-w-full divide-y divide-gray-200 px-4">
                                <thead className="bg-gray-50">
                                    <tr className=''>
                                        <th scope="col" className="border-b border-gray-200" colSpan={8}>
                                            <div className='w-full grid grid-cols-12 gap-2'>
                                                <form className="items-center py-2 col-span-11 px-2 grid grid-cols-12 gap-2">
                                                    <div className='col-span-3 flex'>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button id="date" variant={"outline"}
                                                                    className={cn(
                                                                        "w-[300px] justify-start text-left font-normal",
                                                                        !date && "text-muted-foreground"
                                                                    )}
                                                                >
                                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                                    {date?.from ? (
                                                                        date.to ? (
                                                                            <>
                                                                                {Moment(date.from).format('MMM DD, yyyy')} -{" "}
                                                                                {Moment(date.to).format('MMM DD, yyyy')}
                                                                            </>
                                                                        ) : (
                                                                            Moment(date.from).format('MMM DD, yyyy')
                                                                        )
                                                                    ) : (
                                                                        <span>Pick a date</span>
                                                                    )}
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                <Calendar
                                                                    initialFocus
                                                                    mode="range"
                                                                    defaultMonth={date?.from}
                                                                    selected={date}
                                                                    onSelect={onDateChange}
                                                                    numberOfMonths={2}
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                    <div className='w-full flex col-span-9 items-center'>
                                                        <div className='w-[80px]'>
                                                            <p>Search :</p>
                                                        </div>
                                                        <input type="text" value={query} onChange={handleOnChange} name='search' placeholder="Search by role." className="w-full h-10 px-4 font-sans text-sm outline-none rounded border" />
                                                    </div>
                                                </form>
                                                <div className='col-span-1 flex items-center'>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger>
                                                            <button className="w-full py-1 px-2 h-10 rounded-md bg-prime hover:bg-primeLight text-white mr-4"><i className="las la-file-download mr-2"></i> Export </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            <div
                                                                className='bg-white shadow-md rounded-md absolute' style={{ top: '0px', left: '-64px' }}>
                                                                <div>
                                                                    <ul className='flex flex-col items-start p-2'>
                                                                        <DropdownMenuItem>
                                                                            <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                                exportData('xlsx');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <p className="text-sm font-serif ml-2 font-normal">Export to .xlsx</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem>
                                                                            <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                                exportData('csv');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <p className="text-sm font-serif ml-2 font-normal">Export to .csv</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            USER ID
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Name
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Email ID
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Phone Number
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            User Type
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Status
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            Created At
                                        </th>
                                        <th scope="col" className="px-2 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {
                                        records.length !== 0 && records.map((record) => {
                                            return <tr className="hover:bg-gray-100" key={record._id}>
                                                <td className="px-6 py-4 whitespace-nowrap cursor-pointer">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        <div className='flex items-center text-prime'>
                                                            #{record.userid}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900 uppercase">
                                                        {record.profile.fname} {record.profile.lname}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        {record.profile.email}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        +91 {record.profile.phone}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-prime">
                                                        {record.role}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${record.status == 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {record.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-gray-900">
                                                        {Moment(record.createdAt).format('DD MMM, yyyy hh:mm:ss A')}
                                                    </div>
                                                </td>
                                                <td>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger>
                                                            <motion.i whileHover={{ scale: 1.1 }} class="las la-ellipsis-v text-lg"></motion.i>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            <div>
                                                                <ul className='flex flex-col items-start'>
                                                                    <DropdownMenuItem>
                                                                        <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                            editRecord(record);
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <i className="las la-edit text-lg"></i>
                                                                                <p className='text-sm font-serif ml-2'>Edit</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem>
                                                                        <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                            changePassword(record.userid);
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <i className="las la-lock text-lg"></i>
                                                                                <p className='text-sm font-serif ml-2'>Reset Password</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                    {
                                                                        record.status == 'active' && <DropdownMenuItem>
                                                                            <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                                updateStatus(record.userid, 'banned');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <i className="las la-ban text-lg"></i>
                                                                                    <p className='text-sm font-serif ml-2'>Block</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    }
                                                                    {
                                                                        (record.status == 'banned' || record.status == 'verify') && <DropdownMenuItem>
                                                                            <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                                updateStatus(record.userid, 'active');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <i className="las la-chevron-circle-up text-lg"></i>
                                                                                    <p className='text-sm font-serif ml-2'>Active</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    }
                                                                    {
                                                                        record.status == 'banned' && <DropdownMenuItem>
                                                                            <li className='w-32 cursor-pointer rounded-md' onClick={() => {
                                                                                updateStatus(record.userid, 'deleted');
                                                                            }}>
                                                                                <div className='flex items-center text-base'>
                                                                                    <i className="las la-trash text-lg"></i>
                                                                                    <p className='text-sm font-serif ml-2'>Delete</p>
                                                                                </div>
                                                                            </li>
                                                                        </DropdownMenuItem>
                                                                    }
                                                                </ul>
                                                            </div>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </td>
                                            </tr>
                                        })
                                    }
                                    {
                                        records.length === 0 && <tr className="cursor-pointer hover:bg-gray-100">
                                            <td className="px-6 py-4 whitespace-nowrap" colSpan="8">
                                                <div className="text-sm text-red-600 text-center">
                                                    No data found.
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                            <div className='flex py-2 px-4 items-center bg-blue-50'>
                                <p className='text-sm font-serif'>Show rows per page</p>
                                <select className='text-sm ml-2 bg-gray-100' name="pageSize" onChange={handleOnChange} value={pageSize}>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={40}>40</option>
                                    <option value={50}>50</option>
                                </select>
                                <div className='flex-grow'></div>
                                <p className='text-sm font-serif mr-2'>Page {pageCurrent} of {pageTotal}</p>
                                <div onClick={() => { prevPage() }}><i className={`las la-angle-left w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ${pageCurrent <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                                <div onClick={() => { nextPage() }}><i className={`las la-angle-right w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ml-2 text-black ${pageCurrent === pageTotal ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                            </div>
                        </div>
                    </div>
                </div >
            </ScrollArea>
        </>
    )
}
